<template>
  <bg-card class="button-settings">
    <bg-text size="title-5" class="bg-u-text-flirt-500">
      2. Pengaturan Tombol
    </bg-text>

    <bg-divider class="bg-u-my-md" />

    <bg-text size="title-5" class="bg-u-mb-sm">Jenis Tombol</bg-text>

    <div class="button-settings__options bg-u-mb-md">
      <option-card
        v-for="option in CTA_OPTIONS"
        :key="option.value"
        :label="option.label"
        :active="value.ctaType === option.value"
        :disabled="disabled"
        @click="emitUpdate('ctaType', option.value)"
      >
        <bg-text size="body-2" v-html="option.label" />
      </option-card>
    </div>

    <bg-accordion
      v-for="(button, index) in visibleButtons"
      :id="`button-section-${index}`"
      :key="index"
      :opened="expandedSections[index]"
      class="button-settings__section"
      @toggle-content="toggleSection(index)"
    >
      <template #title>
        <div class="flex align-center">
          <bg-text size="title-5">
            Tombol {{ index === 0 ? 'A' : 'B' }}
          </bg-text>

          <span
            class="button-settings__tag bg-u-ml-xs"
            :class="
              index === 0
                ? 'button-settings__tag--primary'
                : 'button-settings__tag--secondary'
            "
          >
            {{ index === 0 ? 'Utama' : 'Tambahan' }}
          </span>
        </div>
      </template>

      <bg-field label="Label Tombol" class="bg-u-mt-md">
        <bg-input
          :value="button.label"
          placeholder="Masukkan label tombol"
          :disabled="disabled"
          @input="
            updateButton({
              index,
              key: 'label',
              val: $event,
            })
          "
        />
      </bg-field>

      <bg-text size="title-5" class="bg-u-mb-sm">
        Aksi saat Tombol Diklik
      </bg-text>

      <div class="button-settings__options bg-u-mb-md">
        <option-card
          v-for="action in getActionOptions(index)"
          :key="action.value"
          :label="action.label"
          :description="action.description"
          :icon="action.icon"
          :active="button.action === action.value"
          :disabled="disabled || action.disabled"
          @click="
            updateButton({
              index,
              key: 'action',
              val: action.value,
            })
          "
        />
      </div>

      <template v-if="button.action === BUTTON_ACTION.OPEN_LINK">
        <bg-field label="URL Tujuan" class="bg-u-mt-md">
          <bg-input
            :value="button.url"
            placeholder="Masukkan link/URL"
            :disabled="disabled"
            @input="
              updateButton({
                index,
                key: 'url',
                val: $event,
              })
            "
          />
        </bg-field>
      </template>

      <template v-if="button.action === BUTTON_ACTION.FILL_DATA">
        <div class="flex justify-space-between align-center">
          <bg-text size="body-4" class="bg-u-text-neutral-700">
            <em
              >Maksimal {{ MAX_TEXT_FIELDS }} kolom data yang dapat
              ditampilkan</em
            >
          </bg-text>

          <bg-button-icon
            icon="add-plus"
            variant="primary-naked"
            :disabled="disabled || button.textFields.length >= MAX_TEXT_FIELDS"
            @click="addTextField(index)"
          >
            Tambah Kolom
          </bg-button-icon>
        </div>

        <div
          v-for="(field, fieldIndex) in button.textFields"
          :key="fieldIndex"
          class="button-settings__field-row"
        >
          <!-- eslint-disable max-len -->
          <bg-input
            :value="field.title"
            placeholder="Masukkan nama kolom data. Contoh: Nama pemilik"
            class="flex-1"
            :disabled="disabled"
            @input="
              updateField({
                buttonIndex: index,
                fieldIndex,
                key: 'title',
                val: $event,
              })
            "
          />
          <!-- eslint-enable max-len -->

          <bg-select
            :value="field.inputType || 'text'"
            :options="INPUT_TYPE_OPTIONS"
            class="button-settings__field-type"
            :disabled="disabled"
            @input="
              updateField({
                buttonIndex: index,
                fieldIndex,
                key: 'inputType',
                val: $event,
              })
            "
          />

          <bg-button-icon
            v-if="button.textFields.length > 1"
            icon="close"
            size="sm"
            variant="tertiary-naked"
            class="cursor-pointer"
            :disabled="disabled"
            @click.native="removeField(index, fieldIndex)"
          />
        </div>
      </template>
    </bg-accordion>
  </bg-card>
</template>

<script>
import { ref, computed, getCurrentInstance } from '@vue/composition-api';
import {
  BgCard,
  BgText,
  BgDivider,
  BgField,
  BgInput,
  BgButtonIcon,
  BgSelect,
  BgAccordion,
} from 'bangul-vue';

import OptionCard from '@admin_molecules/OptionCard';

import { BUTTON_ACTION } from '../../constants';

const CTA_OPTIONS = [
  { value: 'single', label: '<strong>Single CTA</strong> (1 Tombol)' },
  { value: 'double', label: '<strong>Double CTA</strong> (2 Tombol)' },
];

const ACTION_OPTIONS = [
  {
    value: BUTTON_ACTION.SUBMIT,
    label: 'Submit',
    description: 'Submit data otomatis',
    icon: 'send',
  },
  {
    value: BUTTON_ACTION.OPEN_LINK,
    label: 'Buka Link',
    description: 'Diarahkan ke link/URL',
    icon: 'link',
  },
  {
    value: BUTTON_ACTION.FILL_DATA,
    label: 'Isi Data',
    description: 'Isi data sebelum submit',
    icon: 'check-list',
  },
];

const INPUT_TYPE_OPTIONS = [
  { val: 'text', label: 'Teks' },
  { val: 'number', label: 'Angka' },
];

const MAX_TEXT_FIELDS = 3;

export default {
  name: 'ButtonSettings',

  components: {
    BgCard,
    BgText,
    BgDivider,
    BgField,
    BgInput,
    BgButtonIcon,
    BgSelect,
    BgAccordion,
    OptionCard,
  },

  props: {
    value: {
      type: Object,
      required: true,
    },
    // Read-only mode (e.g. an ended program).
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const { $toast } = getCurrentInstance().proxy;

    const expandedSections = ref([true, true]);

    const visibleButtons = computed(() => {
      if (props.value.ctaType === 'double') {
        return props.value.buttons;
      }

      return props.value.buttons.slice(0, 1);
    });

    const emitUpdate = (key, val) => {
      emit('input', { ...props.value, [key]: val });
    };

    const emitButtonUpdate = buttons => {
      emitUpdate('buttons', buttons);
    };

    const isSecondActionDisabled = (buttonIndex, actionValue) => {
      if (buttonIndex === 0) return false;

      const firstButtonAction = props.value.buttons[0].action;

      if (firstButtonAction === BUTTON_ACTION.FILL_DATA) {
        return actionValue !== BUTTON_ACTION.OPEN_LINK;
      }

      if (firstButtonAction === BUTTON_ACTION.SUBMIT) {
        return actionValue === BUTTON_ACTION.FILL_DATA;
      }

      return false;
    };

    const getActionOptions = buttonIndex =>
      ACTION_OPTIONS.map(option => ({
        ...option,
        disabled: isSecondActionDisabled(buttonIndex, option.value),
      }));

    const updateButton = ({ index, key, val }) => {
      const buttons = props.value.buttons.map((btn, i) =>
        i === index ? { ...btn, [key]: val } : btn
      );

      if (index === 0 && key === 'action') {
        const buttonB = buttons[1];

        // Force Button B to open_link if Button A changes to fill_data.
        if (
          val === BUTTON_ACTION.FILL_DATA &&
          buttonB.action !== BUTTON_ACTION.OPEN_LINK
        ) {
          buttons[1] = {
            ...buttonB,
            action: BUTTON_ACTION.OPEN_LINK,
          };
        }
      }

      // Ensure at least 1 text field when switching to fill_data
      if (key === 'action' && val === BUTTON_ACTION.FILL_DATA) {
        if (!buttons[index].textFields.length) {
          buttons[index] = {
            ...buttons[index],
            textFields: [{ title: '', inputType: 'text' }],
          };
        }
      }

      emitButtonUpdate(buttons);
    };

    const updateField = ({ buttonIndex, fieldIndex, key, val }) => {
      const buttons = props.value.buttons.map((btn, i) => {
        if (i !== buttonIndex) return btn;

        const textFields = btn.textFields.map((field, fi) =>
          fi === fieldIndex ? { ...field, [key]: val } : field
        );

        return { ...btn, textFields };
      });

      emitButtonUpdate(buttons);
    };

    const addTextField = buttonIndex => {
      const buttons = props.value.buttons.map((btn, i) => {
        if (i !== buttonIndex) return btn;
        if (btn.textFields.length >= MAX_TEXT_FIELDS) return btn;

        return {
          ...btn,
          textFields: [...btn.textFields, { title: '', inputType: 'text' }],
        };
      });

      emitButtonUpdate(buttons);
    };

    const removeField = (buttonIndex, fieldIndex) => {
      const btn = props.value.buttons[buttonIndex];
      if (btn.textFields.length <= 1) return;

      const buttons = props.value.buttons.map((b, i) => {
        if (i !== buttonIndex) return b;

        return {
          ...b,
          textFields: b.textFields.filter((_, fi) => fi !== fieldIndex),
        };
      });

      emitButtonUpdate(buttons);

      $toast.show('Kolom berhasil dihapus.');
    };

    const toggleSection = index => {
      expandedSections.value = expandedSections.value.map((val, i) =>
        i === index ? !val : val
      );
    };

    return {
      BUTTON_ACTION,
      CTA_OPTIONS,
      INPUT_TYPE_OPTIONS,
      MAX_TEXT_FIELDS,

      expandedSections,
      visibleButtons,

      emitUpdate,
      getActionOptions,
      updateButton,
      updateField,
      addTextField,
      removeField,
      toggleSection,
    };
  },
};
</script>

<style lang="scss" scoped src="./ButtonSettings.scss" />
